import { useEffect, useCallback, useRef } from "react";
import { ArrowLeft, MessageSquarePlus } from "lucide-react";
import { useDiscussions } from "@/hooks/useDiscussions";
import { DiscussionList } from "./DiscussionList";
import { DiscussionThread } from "./DiscussionThread";
import { DiscussionInput } from "./DiscussionInput";
import { NewDiscussionDialog } from "./NewDiscussionDialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { pageActionButtonClassName, pageActionDesktopOnlyClassName, pageActionLabelClassName } from "@/lib/page-action-button";
import type { Discussion, ReviewRecord } from "@/types";

interface DiscussionsTabProps {
  reviewId: string;
  review: ReviewRecord | null;
  initialDiscussionId?: string;
  newDiscussionOpen?: boolean;
  onNewDiscussionOpenChange?: (open: boolean) => void;
}

export function DiscussionsTab({
  reviewId,
  review,
  initialDiscussionId,
  newDiscussionOpen,
  onNewDiscussionOpenChange,
}: DiscussionsTabProps) {
  const { user } = useAuth();
  const {
    discussions,
    messages,
    activeDiscussion,
    collaborators,
    isLoading,
    fetchDiscussions,
    fetchMessages,
    createDiscussion,
    addMessage,
    markAsRead,
    fetchCollaborators,
    setActiveDiscussion,
    subscribeToDiscussion,
    unsubscribeFromDiscussion,
  } = useDiscussions();

  const fetchedReviewIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (fetchedReviewIdRef.current === reviewId) return;
    fetchedReviewIdRef.current = reviewId;

    fetchDiscussions(reviewId);
    fetchCollaborators(reviewId);
  }, [reviewId, fetchDiscussions, fetchCollaborators]);

  useEffect(() => {
    if (initialDiscussionId && discussions.length > 0 && !activeDiscussion) {
      const targetDiscussion = discussions.find((d) => d.id === initialDiscussionId);
      if (targetDiscussion) {
        setActiveDiscussion(targetDiscussion);
      }
    }
  }, [initialDiscussionId, discussions, activeDiscussion, setActiveDiscussion]);

  useEffect(() => {
    if (activeDiscussion) {
      fetchMessages(activeDiscussion.id);
      subscribeToDiscussion(activeDiscussion.id);
      markAsRead(activeDiscussion.id);
    }
    return () => {
      unsubscribeFromDiscussion();
    };
  }, [activeDiscussion?.id, fetchMessages, subscribeToDiscussion, markAsRead, unsubscribeFromDiscussion]);

  const handleSelectDiscussion = useCallback(
    (discussion: Discussion) => {
      setActiveDiscussion(discussion);
    },
    [setActiveDiscussion],
  );

  const handleCreateDiscussion = useCallback(
    async (title: string, content: string) => {
      const discussion = await createDiscussion(reviewId, { title, content });
      if (discussion) {
        setActiveDiscussion(discussion);
      }
      return !!discussion;
    },
    [reviewId, createDiscussion, setActiveDiscussion],
  );

  const handleSendMessage = useCallback(
    async (content: string, mentions?: string[]) => {
      if (activeDiscussion) {
        await addMessage(activeDiscussion.id, { content, mentions });
      }
    },
    [activeDiscussion, addMessage],
  );

  const currentUserId = user?.id || "";

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1">
        <div
          className={cn(
            "w-full flex-shrink-0 flex-col border-r border-slate-700 bg-slate-900 md:flex md:w-80",
            activeDiscussion ? "hidden" : "flex",
          )}
        >
          <div className="flex items-center justify-between border-b border-slate-700 p-3 md:p-4">
            <h2 className="font-semibold text-white">Threads</h2>
            <NewDiscussionDialog
              onCreateDiscussion={handleCreateDiscussion}
              reviewTable={review?.review_table}
              open={newDiscussionOpen}
              onOpenChange={onNewDiscussionOpenChange}
              trigger={
                <Button
                  size="sm"
                  title="New thread"
                  aria-label="New thread"
                  className={`${pageActionButtonClassName} ${pageActionDesktopOnlyClassName}`}
                >
                  <MessageSquarePlus className="h-4 w-4" />
                  <span className={pageActionLabelClassName}>New</span>
                </Button>
              }
            />
          </div>
          <DiscussionList
            discussions={discussions}
            isLoading={isLoading}
            onSelectDiscussion={handleSelectDiscussion}
            activeDiscussionId={activeDiscussion?.id}
          />
        </div>

        <div
          className={cn(
            "min-w-0 flex-1 flex-col",
            activeDiscussion ? "flex" : "hidden md:flex",
          )}
        >
          {activeDiscussion ? (
            <>
              <div className="flex items-center gap-2 border-b border-slate-700 bg-slate-900 p-3 md:p-4">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 md:hidden"
                  aria-label="Back to threads"
                  onClick={() => setActiveDiscussion(null)}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <h2 className="truncate font-semibold text-white">{activeDiscussion.title}</h2>
                {activeDiscussion.profiles?.full_name ? (
                  <span className="hidden text-xs text-slate-400 sm:inline">
                    started by {activeDiscussion.profiles.full_name}
                  </span>
                ) : null}
              </div>
              <DiscussionThread
                messages={messages}
                isLoading={isLoading}
                currentUserId={currentUserId}
              />
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquarePlus className="mx-auto mb-3 h-12 w-12 opacity-50" />
                <p className="text-sm">Select a thread to view messages</p>
                <p className="mt-1 text-xs">or create a new discussion</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {activeDiscussion ? (
        <div className="shrink-0 overflow-visible border-t border-white/10 bg-slate-900/80 p-3 md:p-4">
          <DiscussionInput
            onSubmit={handleSendMessage}
            collaborators={collaborators}
            currentUserId={currentUserId}
            placeholder="Message"
          />
        </div>
      ) : null}
    </div>
  );
}
